/**
 * ClientModal — Modal para Crear, Editar y Dar de Baja (Soft Delete) Clientes.
 * 
 * Usado en:
 * - ClientsList.tsx (Nuevo Cliente)
 * - ClientDetail.tsx (Editar Cliente y Baja Lógica)
 * - TabBaseDatos.tsx (Editar Datos de Contacto y Plan)
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  UserPlus,
  Settings,
  Building,
  Mail,
  Phone,
  Instagram,
  Globe,
  HardDrive,
  DollarSign,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Trash2,
  RotateCcw,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { Client } from '@/types';

const COLOR_PRESETS = [
  '#6366f1', // Indigo
  '#ec4899', // Pink
  '#8b5cf6', // Violet
  '#3b82f6', // Blue
  '#06b6d4', // Cyan
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#f43f5e', // Rose
  '#14b8a6', // Teal
  '#64748b', // Slate
];

const PLAN_PRESETS: Record<string, { label: string; defaultFee: number }> = {
  basico: { label: 'Básico', defaultFee: 25000 },
  intermedio: { label: 'Intermedio', defaultFee: 45000 },
  premium: { label: 'Premium', defaultFee: 75000 },
};

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (client: Client) => void;
  onDelete?: (clientId: string) => void;
  initialClient?: Client | null;
}

export function ClientModal({
  isOpen,
  onClose,
  onSuccess,
  onDelete,
  initialClient,
}: ClientModalProps) {
  const isEdit = !!initialClient;

  const [form, setForm] = useState({
    name: '',
    business_name: '',
    email: '',
    phone: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    website: '',
    plan_type: 'basico' as Client['plan_type'],
    monthly_fee: '25000',
    start_date: new Date().toISOString().slice(0, 10),
    status: 'active' as Client['status'],
    avatar_color: '#6366f1',
    google_drive_folder_id: '',
    auto_create_drive: false,
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setShowDeleteConfirm(false);
    if (initialClient) {
      setForm({
        name: initialClient.name || '',
        business_name: initialClient.business_name || '',
        email: initialClient.email || '',
        phone: initialClient.phone || '',
        instagram: initialClient.instagram || '',
        facebook: initialClient.facebook || '',
        tiktok: initialClient.tiktok || '',
        website: initialClient.website || '',
        plan_type: initialClient.plan_type || 'basico',
        monthly_fee: String(initialClient.monthly_fee ?? 25000),
        start_date: initialClient.start_date || new Date().toISOString().slice(0, 10),
        status: initialClient.status || 'active',
        avatar_color: initialClient.avatar_color || '#6366f1',
        google_drive_folder_id: initialClient.google_drive_folder_id || '',
        auto_create_drive: false,
        notes: initialClient.notes || '',
      });
    } else {
      setForm({
        name: '',
        business_name: '',
        email: '',
        phone: '',
        instagram: '',
        facebook: '',
        tiktok: '',
        website: '',
        plan_type: 'basico',
        monthly_fee: '25000',
        start_date: new Date().toISOString().slice(0, 10),
        status: 'active',
        avatar_color: '#6366f1',
        google_drive_folder_id: '',
        auto_create_drive: false,
        notes: '',
      });
    }
    setError(null);
  }, [initialClient, isOpen]);

  if (!isOpen) return null;

  const handlePlanChange = (plan: Client['plan_type']) => {
    setForm((prev) => ({
      ...prev,
      plan_type: plan,
      monthly_fee:
        !prev.monthly_fee || prev.monthly_fee === '0' || Object.values(PLAN_PRESETS).some(p => String(p.defaultFee) === prev.monthly_fee)
          ? String(PLAN_PRESETS[plan]?.defaultFee || 25000)
          : prev.monthly_fee,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('El nombre del cliente o contacto es obligatorio.');
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      ...form,
      name: form.name.trim(),
      business_name: form.business_name.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      instagram: form.instagram.trim() || null,
      facebook: form.facebook.trim() || null,
      tiktok: form.tiktok.trim() || null,
      website: form.website.trim() || null,
      monthly_fee: parseFloat(form.monthly_fee) || 0,
      notes: form.notes.trim() || null,
      google_drive_folder_id: form.google_drive_folder_id.trim() || null,
    };

    try {
      if (isEdit && initialClient) {
        const res = await api.updateClient(initialClient.id, {
          ...initialClient,
          ...payload,
        });
        const updated = (res as any).client || {
          ...initialClient,
          ...payload,
        };
        onSuccess(updated);
      } else {
        const res = await api.createClient(payload);
        const created = (res as any).client || {
          id: res.id,
          ...payload,
          pending_tasks: 0,
          monthly_hours_seconds: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        onSuccess(created);
      }
      onClose();
    } catch (err: any) {
      console.error('Error guardando cliente:', err);
      setError(err.message || 'Ocurrió un error al guardar los datos del cliente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSoftDelete = async () => {
    if (!initialClient) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteClient(initialClient.id);
      if (onDelete) {
        onDelete(initialClient.id);
      } else {
        onSuccess({ ...initialClient, status: 'inactive' });
      }
      onClose();
    } catch (err: any) {
      console.error('Error en baja lógica del cliente:', err);
      setError(err.message || 'Error al eliminar el cliente');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold shadow-xs"
              style={{ backgroundColor: form.avatar_color }}
            >
              {isEdit ? <Settings size={18} /> : <UserPlus size={18} />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-900 text-base">
                  {isEdit ? `Editar Cliente: ${initialClient.name}` : 'Nuevo Cliente'}
                </h3>
                {isEdit && initialClient.status === 'inactive' && (
                  <span className="px-2 py-0.5 bg-slate-200 text-slate-700 font-bold rounded text-[10px] uppercase tracking-wider">
                    Inactivo
                  </span>
                )}
                {isEdit && initialClient.status === 'paused' && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 font-bold rounded text-[10px] uppercase tracking-wider">
                    Pausado
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {isEdit
                  ? 'Actualiza los datos de contacto, plan y configuración del cliente'
                  : 'Ingresa los datos del cliente para comenzar la gestión de marketing'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 rounded-lg p-1.5 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-medium">
              <AlertCircle size={16} className="text-rose-600 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Confirmación de Baja Lógica (Soft Delete) */}
          {showDeleteConfirm && initialClient && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl space-y-3 animate-in fade-in">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertTriangle size={18} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-bold text-rose-900">
                    ¿Confirmas dar de baja a {initialClient.name}?
                  </h4>
                  <p className="text-[11px] text-rose-700 leading-relaxed">
                    Se realizará una <strong>baja lógica (soft delete)</strong>: el cliente pasará al estado <strong>Inactivo</strong>. Sus tareas, registros de tiempo, copys y carpetas de Google Drive <strong>no se borrarán</strong> y podrás consultarlo o reactivarlo en cualquier momento desde el filtro de clientes.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-rose-200/60">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="text-xs h-7 border-rose-200 text-rose-800 hover:bg-rose-100/50"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSoftDelete}
                  disabled={deleting}
                  className="text-xs h-7 bg-rose-600 hover:bg-rose-700 text-white gap-1 shadow-xs"
                >
                  {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  {deleting ? 'Dando de baja...' : 'Sí, eliminar (baja lógica)'}
                </Button>
              </div>
            </div>
          )}

          {/* Aviso si está Inactivo con opción de reactivar rápido */}
          {isEdit && form.status === 'inactive' && !showDeleteConfirm && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertCircle size={15} className="text-amber-600 shrink-0" />
                <span>Este cliente está <strong>Inactivo</strong> (archivado con baja lógica).</span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setForm((prev) => ({ ...prev, status: 'active' }))}
                className="h-6 text-[11px] border-amber-300 text-amber-900 hover:bg-amber-100 gap-1"
              >
                <RotateCcw size={11} /> Reactivar a Activo
              </Button>
            </div>
          )}

          {/* ─── 1. Identificación y Avatar ─── */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
              <Building size={14} className="text-primary" />
              Identificación del Cliente
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Nombre de Contacto / Titular <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Silvia Gómez"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Nombre Comercial / Marca / Negocio
                </label>
                <input
                  type="text"
                  placeholder="Ej: Estética Silvia & Spa"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>
            </div>

            {/* Selector de Color de Avatar */}
            <div className="mt-3.5">
              <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                Color de Identificación
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {COLOR_PRESETS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setForm({ ...form, avatar_color: color })}
                    className={`w-7 h-7 rounded-full transition-transform ${
                      form.avatar_color === color
                        ? 'ring-2 ring-offset-2 ring-primary scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
                <div className="flex items-center gap-1.5 ml-2">
                  <input
                    type="color"
                    value={form.avatar_color}
                    onChange={(e) => setForm({ ...form, avatar_color: e.target.value })}
                    className="w-7 h-7 rounded-lg border border-slate-200 cursor-pointer p-0 bg-transparent"
                    title="Color personalizado"
                  />
                  <span className="text-xs text-slate-400 font-mono">{form.avatar_color}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ─── 2. Plan y Facturación ─── */}
          <div className="pt-4 border-t border-slate-100">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
              <DollarSign size={14} className="text-emerald-500" />
              Plan Contratado y Honorarios
            </h4>

            {/* Selector de Plan */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {(['basico', 'intermedio', 'premium'] as const).map((plan) => {
                const isSelected = form.plan_type === plan;
                return (
                  <button
                    key={plan}
                    type="button"
                    onClick={() => handlePlanChange(plan)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-xs'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold capitalize text-slate-800">
                        {PLAN_PRESETS[plan].label}
                      </span>
                      {isSelected && <CheckCircle2 size={14} className="text-primary" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      ${PLAN_PRESETS[plan].defaultFee.toLocaleString('es-AR')}/mes
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Honorarios Mensuales ($ ARS)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-slate-400 font-bold">$</span>
                  <input
                    type="number"
                    min="0"
                    step="500"
                    placeholder="25000"
                    value={form.monthly_fee}
                    onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary font-semibold"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Fecha de Inicio
                </label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Estado del Cliente
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as Client['status'] })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                >
                  <option value="active">Activo (En gestión habitual)</option>
                  <option value="paused">Pausado (Temporalmente sin tareas)</option>
                  <option value="inactive">Inactivo (Baja lógica / Archivador)</option>
                </select>
              </div>
            </div>
          </div>

          {/* ─── 3. Contacto y Redes Sociales ─── */}
          <div className="pt-4 border-t border-slate-100">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
              <Instagram size={14} className="text-pink-500" />
              Contacto y Redes Sociales
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  <Mail size={12} className="text-slate-400" /> Email
                </label>
                <input
                  type="email"
                  placeholder="cliente@ejemplo.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  <Phone size={12} className="text-slate-400" /> Teléfono / WhatsApp
                </label>
                <input
                  type="tel"
                  placeholder="+54 9 11 1234-5678"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  <Instagram size={12} className="text-pink-500" /> Instagram (@usuario)
                </label>
                <input
                  type="text"
                  placeholder="@usuario"
                  value={form.instagram}
                  onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1 flex items-center gap-1">
                  <Globe size={12} className="text-blue-500" /> Sitio Web
                </label>
                <input
                  type="text"
                  placeholder="https://marca.com"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Facebook Fanpage
                </label>
                <input
                  type="text"
                  placeholder="https://facebook.com/pagina"
                  value={form.facebook}
                  onChange={(e) => setForm({ ...form, facebook: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  TikTok (@usuario)
                </label>
                <input
                  type="text"
                  placeholder="@marca.tiktok"
                  value={form.tiktok}
                  onChange={(e) => setForm({ ...form, tiktok: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
          </div>

          {/* ─── 4. Google Drive ─── */}
          <div className="pt-4 border-t border-slate-100">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
              <HardDrive size={14} className="text-blue-500" />
              Integración Google Drive
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Link o ID de Carpeta en Google Drive (Opcional)
                </label>
                <input
                  type="text"
                  placeholder="https://drive.google.com/drive/folders/1ABC... o 1ABC..."
                  value={form.google_drive_folder_id}
                  onChange={(e) => setForm({ ...form, google_drive_folder_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Puedes pegar el enlace completo de la carpeta compartida o su ID.
                </p>
              </div>

              {!isEdit && (
                <label className="flex items-start gap-2.5 p-3 rounded-xl border border-blue-100 bg-blue-50/50 cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.auto_create_drive}
                    onChange={(e) => setForm({ ...form, auto_create_drive: e.target.checked })}
                    className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="text-xs">
                    <span className="font-semibold text-slate-800 block">
                      Crear automáticamente carpeta del cliente en Google Drive
                    </span>
                    <span className="text-slate-500 text-[11px]">
                      Si tienes Google Drive conectado en Teresita, creará la carpeta con subcarpetas para el período actual.
                    </span>
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* ─── 5. Notas Adicionales ─── */}
          <div className="pt-4 border-t border-slate-100">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
              <FileText size={14} className="text-slate-400" />
              Notas Internas
            </h4>
            <textarea
              rows={2}
              placeholder="Anotaciones importantes, requerimientos especiales del cliente, etc."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100 sticky bottom-0 bg-white/95 backdrop-blur-xs">
            {/* Botón de Borrar (Baja Lógica) en modo Edición */}
            {isEdit && !showDeleteConfirm ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading || deleting}
                className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 gap-1.5"
              >
                <Trash2 size={13} />
                Eliminar Cliente
              </Button>
            ) : (
              <div />
            )}

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading || deleting}
                className="text-xs"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || deleting}
                className="text-xs gap-1.5 bg-primary hover:bg-primary/90 text-white shadow-sm"
              >
                {loading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {isEdit ? 'Guardando...' : 'Creando cliente...'}
                  </>
                ) : isEdit ? (
                  'Guardar Cambios'
                ) : (
                  <>
                    <UserPlus size={14} />
                    Crear Cliente
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
